import classNames from 'classnames';
import { useMemo } from 'react';

import { ActionEvent, ActionTargetType, ActionType, Product } from '../../../../types/working-model';
import { Button } from '../../../general-ui/button/button';
// import { ColorList } from '../../../general-ui/color-list/color-list';
import { ImagePreview } from '../../../general-ui/image-preview/image-preview';
import ModalComponent from '../../../general-ui/modal/modal';
import { TypographyItem } from '../../../general-ui/typography-item/typography-item';
import { stylingOverrideReturns } from '../../utils';
import { useTracking } from '../../../../utils/tracking';
import '../../../../scss/live-event/base/containers/product-details-modal.scss';
import useTranslationRoute from '../../hooks/use-translation-route';
import { useTranslate } from '../../../../i18n/useTranslationModules';

interface Props {
	product: Product;
	template: string;
	layout_type: unknown;
	stylingOverrides: stylingOverrideReturns;
	onClose: () => void;
	language: string
}
export const ProductDetailsModal = ({
	product,
	stylingOverrides,
	template,
	onClose,
	language
}: Props): JSX.Element => {
	const {
		price,
		sale_price,
		product_title,
		description,
		images,
		button_text,
		buy_now_link,
		cta_new_tab,
		module_id: id
	} = product;
	const translationRoute = useTranslationRoute();
	const { t } = useTranslate(translationRoute);
	const {
		moduleTitleOverrides,
		moduleCustomHeadingOverrides,
		moduleDescriptionOverrides,
		primaryButtonOverrides,
	} = stylingOverrides;

	const buyNowLink = useMemo(() => {
		if (!buy_now_link) return null;

		if (!buy_now_link.startsWith('http')) {
			return `https://${buy_now_link}`;
		}

		return buy_now_link;
	}, [buy_now_link]);

	const { trackEvent } = useTracking();
	const transKey = `${translationRoute}:product.${id}`;
	const defaultButtonText = t('session:Buy Now');
	return (
		<ModalComponent
			className={classNames(
				'ev-modal-wrapper live-event-container product-details-modal',
				template
			)}
			title=""
			open={true}
			onRequestClose={onClose}
			closeable
			cancellable
			eventPreview
		>
			<div className="product-details-modal-content">
				<div className="product-details-gallery">
					<ImagePreview
						options={images.map((imageUrl) => ({ imageUrl }))}
						arialLabel={(product_title?.[language] ?? product_title?.base ?? '') as string}
						template={template}
					/>
				</div>

				<div className="product-details-content">
					<TypographyItem
						className={classNames('product-details-title stable', moduleTitleOverrides)}
						tagName="h3"
					>
						{t(`${transKey}.title`, product_title[language] as string ?? '')}
					</TypographyItem>
					<TypographyItem className={classNames('product-details-label stable')}>
						{/* TODO: Add section label */}
					</TypographyItem>
					{/* out of scope for MVP */}
					{/* <ColorList label="Color" template={template}/> */}

					<div className="product-details-price-container">
						<TypographyItem
							className={classNames('product-details-price stable', moduleCustomHeadingOverrides)}
						>
							{price?.base ? (
								`$ ${t(`${transKey}.sale_price`, (sale_price?.[language] || price?.[language]) ?? '')}`
							) : null}
						</TypographyItem>
						{(sale_price?.base && price?.base) ? (
							<TypographyItem className={classNames('product-details-old-price evt-body-text-2 stable', moduleDescriptionOverrides)}>
								$ {t(`${transKey}.price`, price?.[language] ?? '')}
							</TypographyItem>
						) : null}
					</div>
					<TypographyItem className={classNames('product-details-description evt-body-text-2 stable', moduleDescriptionOverrides)} tagName="p">
						{t(`${transKey}.description`, description?.[language] as string)}
					</TypographyItem>
					{buyNowLink && (
						<Button
							onClick={() => trackEvent({
								action: ActionEvent.Click,
								action_type: ActionType.Active,
								target_type: ActionTargetType.Product,
								target_id: product?.product
							})}
							classButton={classNames(
								'primary',
								primaryButtonOverrides
							)}
							template={template}
							url={buyNowLink}
							tagName="a"
							newTab={cta_new_tab !== false}
						>
							{t(`${transKey}.button_text`, button_text?.[language] as string || defaultButtonText)}
						</Button>
					)}
				</div>
			</div>
		</ModalComponent>
	);
};
